/**
 * Team invite/edit modals only offer the apps switched on in
 * Product settings -> Applications (company app ids from GET /company/app).
 * L'Marketplace has no toggle there, so it stays manager-only.
 */
const COMPANY_APP_ID_TO_TEAM_APP_KEY = {
  1: "ltax",
  2: "lepr",
  3: "linvoice",
  6: "lauthorize",
};

/** app_country keys from invitation/member payloads → numeric app ids. */
const APP_COUNTRY_KEY_TO_APP_ID = {
  tax: 1,
  EPR: 2,
  epr: 2,
  inv: 3,
  auth: 6,
};

const getCompanyAppIdList = (companyApp) => {
  const list = Array.isArray(companyApp)
    ? companyApp
    : Object.values(companyApp || {});

  return list
    .map((appId) => Number(appId))
    .filter((appId) => Number.isFinite(appId) && appId > 0);
};

/**
 * @param {Array|object} companyApp - /company/app payload: ["1", "2"] or { 0: "1" }
 * @returns {Set<number>} app ids enabled on the Applications page
 */
export const getEnabledCompanyAppIds = (companyApp) => {
  return new Set(
    getCompanyAppIdList(companyApp).filter((appId) => COMPANY_APP_ID_TO_TEAM_APP_KEY[appId])
  );
};

/**
 * @param {Array|object} companyApp - /company/app payload: ["1", "2"] or { 0: "1" }
 * @returns {Set<string>} team app keys enabled for the company
 */
export const getEnabledTeamAppKeys = (companyApp) => {
  return new Set(
    getCompanyAppIdList(companyApp)
      .map((appId) => COMPANY_APP_ID_TO_TEAM_APP_KEY[appId])
      .filter(Boolean)
  );
};

/**
 * @param {object} appCountry - member app_country map, e.g. { tax: ["DE"], inv: [] }
 * @returns {Set<number>} app ids assigned to that member
 */
export const getAssignedAppIdsFromAppCountry = (appCountry) => {
  if (!appCountry || typeof appCountry !== "object" || Array.isArray(appCountry)) {
    return new Set();
  }

  const ids = new Set();
  Object.keys(appCountry).forEach((key) => {
    const mapped =
      APP_COUNTRY_KEY_TO_APP_ID[key] ??
      APP_COUNTRY_KEY_TO_APP_ID[String(key).toLowerCase()];
    if (mapped) ids.add(mapped);
  });
  return ids;
};

export const findTeamMemberByUserId = (users, userId) => {
  if (userId == null || !Array.isArray(users)) return null;
  const id = Number(userId);
  if (!Number.isFinite(id)) return null;

  return users.find((row) => Number(row?.user?.id ?? row?.id) === id) ?? null;
};

/**
 * Other Lappa Apps: drop the current app, then keep only company-enabled
 * apps that are also assigned to this user. Managers see every switcher app.
 * Until company or assignment data exists, show nothing (avoid flashing apps
 * the user cannot open).
 */
export const getVisibleSwitcherProjects = ({
  projects,
  currentProjectName,
  isManager,
  companyAppIds,
  assignedAppIds,
}) => {
  const list = Array.isArray(projects) ? projects : [];
  const otherProjects = list.filter((project) => project.name !== currentProjectName);

  if (isManager) return otherProjects;

  const companyIds = companyAppIds instanceof Set ? companyAppIds : new Set();
  const userIds = assignedAppIds instanceof Set ? assignedAppIds : new Set();

  if (companyIds.size === 0 && userIds.size === 0) {
    return [];
  }

  return otherProjects.filter((project) => {
    const id = Number(project.appId);
    if (companyIds.size > 0 && !companyIds.has(id)) return false;
    if (userIds.size > 0 && !userIds.has(id)) return false;
    return true;
  });
};
